// Protocolo do Hábito — o herói da Home.
//
// A Home deixa de ser uma lista de cards e passa a ser a renderização do estado
// do dia. Um organismo só, com um caminho só: abrir o livro.
//
// Invariantes que este arquivo carrega:
//   3 · zero decisões — o CTA abre o livro na página certa, sem tela no meio
//   4 · nunca mostrar zero — a sequência jamais aparece sozinha
//   5 · premiar o retorno — o estado RETORNO tem tela própria e prioridade
//   7 · nunca prometer dias — "dia 3 de 67" é contagem, jamais promessa de resultado

import React from 'react';
import { BookCover } from '../common';
import { Icon } from '../icons';
import { OverlayStoryModal, BotaoOverlayStory } from '../overlay-story';
import { BookV2 } from '../../types';
import { EstadoHabito } from './api';

const serif = 'Instrument Serif, Georgia, serif';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600,
};

/* ── As marcas permanentes ────────────────────────────────────────────────
   A conquista é permanente; o contador é que reinicia. Por isso a sequência
   atual NUNCA viaja sozinha — e quando ela é zero, simplesmente não existe
   número nenhum na tela: o que aparece são os dias lidos e a melhor marca,
   que ninguém tira de você. */
const Marcas: React.FC<{ seq: { atual: number; melhor: number; diasLidos: number }; escuro?: boolean }> = ({ seq, escuro }) => {
  const fraco = escuro ? 'rgba(245,241,234,0.55)' : 'var(--ink-3)';
  const forte = escuro ? '#F5F1EA' : 'var(--ink)';
  if (!seq.diasLidos) return null;

  // O recorde NUNCA some. Antes, `melhor !== atual` retirava o item exatamente
  // no dia em que a pessoa igualava a própria marca — o único momento de
  // superação que o app calcula era renderizado como perda visual.
  const noRecorde = seq.atual > 1 && seq.atual >= seq.melhor;
  const itens: { n: number; r: string; destaque?: boolean }[] = [];
  if (seq.atual > 0) itens.push({ n: seq.atual, r: seq.atual === 1 ? 'dia seguido' : 'dias seguidos' });
  itens.push({ n: seq.diasLidos, r: seq.diasLidos === 1 ? 'dia lido' : 'dias lidos' });
  if (seq.melhor > 1) itens.push({ n: seq.melhor, r: noRecorde ? 'melhor marca · agora' : 'melhor marca', destaque: noRecorde });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      {seq.atual > 0 && <Icon name="flame" size={22} color="var(--ember)" />}
      {itens.map((it, k) => (
        <div key={k}>
          <div style={{ fontFamily: serif, fontSize: 23, lineHeight: 1, color: it.destaque ? 'var(--ember)' : forte }}>{it.n}</div>
          <div style={{ ...eyebrow, fontSize: 9.5, color: it.destaque ? 'var(--ember)' : fraco, marginTop: 3 }}>{it.r}</div>
        </div>
      ))}
    </div>
  );
};

/* ── A semana — frequência é a métrica-mãe ─────────────────────────────── */
const Semana: React.FC<{ dias: string[]; hoje: string; escuro?: boolean }> = ({ dias, hoje, escuro }) => {
  const rotulos = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
  const base = new Date(hoje + 'T12:00:00Z');
  const diaSemana = base.getUTCDay();
  const domingo = new Date(base); domingo.setUTCDate(base.getUTCDate() - diaSemana);

  return (
    <div style={{ display: 'flex', gap: 7 }}>
      {rotulos.map((r, k) => {
        const d = new Date(domingo); d.setUTCDate(domingo.getUTCDate() + k);
        const chave = d.toISOString().slice(0, 10);
        const leu = dias.includes(chave);
        const eHoje = chave === hoje;
        const futuro = chave > hoje;
        return (
          <div key={k} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 1 }}>
            <div style={{
              width: '100%', height: 5, borderRadius: 999,
              background: leu ? 'var(--ember)' : (escuro ? 'rgba(245,241,234,0.14)' : 'var(--paper-3)'),
              opacity: futuro ? 0.4 : 1,
              boxShadow: eHoje && leu ? '0 0 10px var(--ember)' : 'none',
            }} />
            <div style={{
              fontSize: 9.5, letterSpacing: '0.06em',
              color: eHoje ? 'var(--ember)' : (escuro ? 'rgba(245,241,234,0.4)' : 'var(--ink-4)'),
              fontWeight: eHoje ? 700 : 500,
            }}>{r}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ── A linha do contrato ───────────────────────────────────────────────── */
const LinhaContrato: React.FC<{ contrato: NonNullable<EstadoHabito['contrato']>; escuro?: boolean }> = ({ contrato, escuro }) => (
  <div style={{
    display: 'flex', gap: 10, alignItems: 'flex-start',
    padding: '13px 15px', borderRadius: 15,
    background: escuro ? 'rgba(245,241,234,0.07)' : 'var(--paper)',
    border: `1px solid ${escuro ? 'rgba(245,241,234,0.10)' : 'var(--line)'}`,
  }}>
    <Icon name="target" size={15} color="var(--ember)" style={{ marginTop: 2, flexShrink: 0 }} />
    <div style={{
      fontSize: 14.5, lineHeight: 1.5,
      color: escuro ? 'rgba(245,241,234,0.85)' : 'var(--ink-2)',
    }}>
      {contrato.quandoKind === 'horario' ? 'Todo dia ' : 'Assim que você '}
      <span style={{ color: 'var(--ember)' }}>{contrato.quando.replace(/^assim que eu /, '')}</span>
      , você lê <span style={{ color: 'var(--ember)' }}>{contrato.onde}</span>.
    </div>
  </div>
);

/* ── O herói ───────────────────────────────────────────────────────────── */
export const HeroProtocolo: React.FC<{
  estado: EstadoHabito;
  diasComCheckin: string[];
  hoje: string;
  onAbrirLivro: () => void;
  onComecarProtocolo: () => void;
  onVerPrograma?: () => void;
  onTrocarLivro?: () => void;
  /** Atalho para a Pagina do Livro. Sem isto, capa e titulo parecem
   *  clicaveis e nao sao — e o unico caminho e voltar pela estante. */
  onVerLivro?: (bookId: string) => void;
  ofereceTroca?: boolean;
  /** Livro do ÚLTIMO REGISTRO, vindo da estante. Quando existe, é ele que o
   *  card mostra e é ele que o botão abre — o card acompanha a pessoa. */
  livroAtual?: BookV2 | null;
  /* ── O card é de TODO MUNDO, inclusive de quem não tem programa ───────
     A ofensiva do protocolo nasce dos check-ins, e hoje só 5 contas têm
     algum — 7 pessoas que leem no app não teriam nenhum. Sem estas três
     entradas, o card mostraria a essas pessoas uma chama apagada e um dia
     em aberto que elas já venceram: progresso real sumindo da tela.
     Regra: o card nunca mostra MENOS do que a pessoa fez. Quando o programa
     tem números próprios, são eles que valem. */
  sequenciaAlternativa?: { atual: number; melhor: number; diasLidos: number };
  leuHojeAlternativo?: boolean;
  paginasHojeAlternativo?: number;
}> = ({ estado, diasComCheckin, hoje, onAbrirLivro, onComecarProtocolo, onVerPrograma, onTrocarLivro, onVerLivro, ofereceTroca, livroAtual,
        sequenciaAlternativa, leuHojeAlternativo, paginasHojeAlternativo }) => {
  const { livro, contrato, dia, diaMarco, sequencia, estagio } = estado;
  const [overlayAberto, setOverlayAberto] = React.useState(false);
  // Depois da formatura o contador do programa SOME — ele já cumpriu a função.
  // O contrato fica, os números permanentes ficam, a cobrança vai embora.
  const formado = estado.estado === 'FORMADO';

  // SEM PROGRAMA: nunca se inscreveu, está explorando, pausou ou dispensou.
  // O card é o mesmo — o que muda é que não existe dia, contrato nem estágio
  // para mostrar, e o estado do dia vem do app, não do protocolo.
  const semPrograma = !dia && !contrato;

  // Sem programa, o protocolo nunca diz DIA_VENCIDO — a pessoa leria hoje e o
  // card continuaria dizendo "hoje ainda está em aberto".
  const venceu = estado.estado === 'DIA_VENCIDO'
    || (formado && !!estado.leuHoje)
    || (semPrograma && !!leuHojeAlternativo);
  const escuro = venceu;

  // O card nunca mostra MENOS do que a pessoa fez: quando o programa não tem
  // números, valem os do app.
  const seq = (sequencia.diasLidos > 0 || !sequenciaAlternativa) ? sequencia : sequenciaAlternativa;

  // O programa tem UM livro; a pessoa costuma ter mais de um aberto. Quando a
  // Home sabe qual foi o último registro, é esse que o card mostra — senão o
  // card fica preso no livro do contrato enquanto a pessoa lê outro, e o botão
  // abre o livro errado. O programa em si não muda: só a vitrine acompanha.
  const mostrado = livroAtual
    ? {
        id: livroAtual.id,
        titulo: livroAtual.title,
        autor: livroAtual.author,
        capaUrl: livroAtual.coverUrl,
        paginaAtual: livroAtual.read || 0,
        total: livroAtual.totalPages || 0,
        capa: livroAtual as any,
      }
    : livro
      ? {
          id: livro.id,
          titulo: livro.titulo,
          autor: livro.autor,
          capaUrl: livro.capaUrl,
          paginaAtual: livro.paginaAtual,
          total: livro.totalPaginas || 0,
          capa: { title: livro.titulo, author: livro.autor, coverUrl: livro.capaUrl, coverHue: livro.capaHue, coverStyle: livro.capaEstilo } as any,
        }
      : null;
  // "Qual é o próximo?" é resposta para quem ficou sem livro. Com um livro em
  // leitura na mão, a pergunta vira um beco.
  const precisaEscolher = !!estado.precisaEscolherLivro && !livroAtual;

  // O card à parte de quem está "explorando" saiu: existe UMA Home, e quem não
  // tem programa vê o mesmo card que todo mundo, com o livro e a ação. O convite
  // para entrar no protocolo continua, numa linha discreta abaixo do card.

  // Passado o marco, "de 67" vira mentira aritmética ("Dia 75 de 67"). O
  // programa continua — o denominador é que deixa de fazer sentido.
  const rotuloEstagio = formado
    ? 'A Identidade'
    : (dia
        ? (dia > diaMarco ? `Dia ${dia} · ${estagio.nome}` : `Dia ${dia} de ${diaMarco} · ${estagio.nome}`)
        : estagio.nome);

  // Ontem foi coberto por uma proteção? A pessoa precisa saber que foi o perdão
  // que segurou a sequência, e não sorte.
  const ontem = (() => { const d = new Date(hoje + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); })();
  const protegeuOntem = !!estado.protecoes?.dias?.includes(ontem);

  const notaDoEstagio = ({
    ignicao: 'As primeiras repetições valem mais que as últimas — estes dias carregam o resultado inteiro.',
    vale: 'Este é o trecho em que quase todo mundo para. Não precisa ser bonito, precisa acontecer.',
    transferencia: 'Daqui pra frente eu te lembro cada vez menos, de propósito: o gatilho tem que virar o lugar, não o celular. E os ganhos ficam menores agora — é assim que a curva funciona.',
    consolidacao: 'O contador importa menos daqui em diante. O que estamos medindo agora é se ler já acontece sozinho.',
    identidade: 'Sem programa e sem contador. O contrato continua valendo, e eu só apareço quando você me chamar.',
  } as Record<string, string>)[estagio.chave] || null;

  return (
    <div style={{ padding: '24px 20px 0' }}>
      <div style={{
        // Cor LITERAL de propósito: no tema midnight `--ink` é o creme (é a cor
        // do texto sobre papel escuro), então usar a variável aqui pintaria
        // creme sobre creme. Card escuro nunca usa --ink/--paper.
        background: escuro ? '#1A1614' : 'var(--paper-2)',
        border: `1px solid ${escuro ? 'var(--ember)' : 'var(--line)'}`,
        borderRadius: 26, padding: 22, position: 'relative', overflow: 'hidden',
        transition: 'background 600ms',
        boxShadow: escuro ? '0 20px 44px -14px rgba(199,83,26,0.38)' : 'none',
      }}>
        <svg width="420" height="260" style={{ position: 'absolute', top: -40, right: -120, opacity: 0.06, pointerEvents: 'none' }}>
          <circle cx="210" cy="130" r="180" fill="none" stroke="var(--ember)" strokeWidth="40" />
        </svg>

        {/* A tarja do estágio só existe para quem está no programa. Sem programa
            ela viraria "A Ignição" — o nome de um estágio de um percurso que a
            pessoa não começou — com um atalho para uma tela de programa vazia. */}
        {!semPrograma && (
          <button onClick={onVerPrograma} disabled={!onVerPrograma} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            padding: 0, cursor: onVerPrograma ? 'pointer' : 'default', position: 'relative', zIndex: 2,
          }}>
            <span className="mono" style={{ ...eyebrow, color: escuro ? 'rgba(245,241,234,0.6)' : 'var(--ink-3)' }}>
              {rotuloEstagio}
            </span>
            {onVerPrograma && <Icon name="chevron-right" size={13} color={escuro ? 'rgba(245,241,234,0.5)' : 'var(--ink-4)'} />}
          </button>
        )}

        {/* Compartilhar o protocolo. Fica no canto do card, como no card do
            Leitor — o gesto é o mesmo, então o lugar tem que ser o mesmo.
            Aparece desde o dia 1: começar é justamente o que dá vontade de
            contar, e é o momento em que o compromisso público mais ajuda a
            pessoa a voltar amanhã. */}
        <BotaoOverlayStory
          onClick={() => setOverlayAberto(true)}
          top={16} right={16} caixa={34}
          fundo={escuro ? 'rgba(245,241,234,0.10)' : 'var(--paper-3)'}
        />

        {/* ── Dia vencido ─────────────────────────────────────────────── */}
        {venceu && (<>
          <div style={{ fontFamily: serif, fontSize: 34, lineHeight: 1.1, color: '#F5F1EA', margin: '12px 0 6px' }}>
            {formado ? 'Mais um dia.' : 'Está feito.'}
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(245,241,234,0.75)', margin: '0 0 18px' }}>
            {/* "O dia está vencido" é ambíguo em português: lê como PRAZO
                expirado, não como dia ganho. A voz ativa não tem esse problema. */}
            {(() => {
              // Sem programa não existe `hojeLido`; o número vem do app, senão
              // quem leu 12 páginas hoje lê um "você venceu o dia" mudo.
              const n = estado.hojeLido?.paginas || (semPrograma ? paginasHojeAlternativo : 0) || 0;
              return n ? `Você leu ${n} ${n === 1 ? 'página' : 'páginas'} hoje.` : 'Você venceu o dia.';
            })()}
            {formado ? ' Sem contador, sem cobrança — só a leitura.' : ' Amanhã de novo, no mesmo lugar.'}
          </p>
        </>)}

        {/* ── Dia em aberto ───────────────────────────────────────────── */}
        {!venceu && (<>
          {/* "Hoje ainda está em aberto." vai numa linha só: mede 311px e o
              card dá 344, então a quebra era escolha, não limite — e custava
              uma linha de altura no topo. As outras duas mantêm a quebra:
              "já traz você de volta" não cabe, e ali o corte é a ênfase. */}
          <div style={{ fontFamily: serif, fontSize: 33, lineHeight: 1.1, color: 'var(--ink)', margin: '12px 0 14px' }}>
            {estado.estado === 'RETORNO' ? <>Uma página<br />já <i>traz você de volta</i>.</>
              : estado.estado === 'AGUARDANDO_JANELA' ? <>Seu momento<br />ainda vem hoje.</>
              : <>Hoje ainda está <i>em aberto</i>.</>}
          </div>
          {contrato && <LinhaContrato contrato={contrato} />}

          {/* ── O LIVRO, EM TAMANHO DE LIVRO ───────────────────────────────
              A capa tinha 46px de largura num canto, do tamanho de um ícone,
              e o objeto do dia inteiro aparecia como enfeite de uma linha de
              texto. Aqui ela ocupa quase metade do card e vira o herói: é a
              coisa que a pessoa vai pegar na mão daqui a um minuto.
              À direita, na mesma altura da capa, a informação em ordem de
              leitura — título, autor, onde parou, quanto andou — e a ação no
              fim, onde o olho já está. */}
          {mostrado && !precisaEscolher && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', marginTop: 18 }}>
              <div
                onClick={() => onVerLivro && mostrado.id && onVerLivro(mostrado.id)}
                role={onVerLivro ? 'button' : undefined}
                style={{ cursor: onVerLivro ? 'pointer' : 'default', flexShrink: 0 }}
              >
                <BookCover book={mostrado.capa} width={132} height={198} />
              </div>

              {/* A coluna é centrada contra a capa, não esticada até as duas
                  pontas: com título curto, ancorar em cima e embaixo abria uma
                  faixa morta de uns 60px no meio — o mesmo defeito de proporção
                  que este redesenho veio consertar. */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div
                  onClick={() => onVerLivro && mostrado.id && onVerLivro(mostrado.id)}
                  role={onVerLivro ? 'button' : undefined}
                  style={{ cursor: onVerLivro ? 'pointer' : 'default' }}
                >
                  <div style={{
                    fontSize: 17, lineHeight: 1.2, fontWeight: 700, color: 'var(--ink)',
                    // Três linhas e para. Título longo empurrando o botão para
                    // fora do card é pior do que título cortado.
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {mostrado.titulo}
                  </div>
                  {mostrado.autor && (
                    <div className="mono" style={{
                      fontSize: 10, letterSpacing: '0.13em', textTransform: 'uppercase',
                      color: 'var(--ink-3)', marginTop: 6,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {mostrado.autor}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ height: 1, background: 'var(--line)', marginBottom: 11 }} />
                  <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                    {mostrado.paginaAtual > 0
                      ? `Você parou na página ${mostrado.paginaAtual}`
                      : 'Ainda não começou'}
                  </div>

                  {/* A barra é do LIVRO, não do programa. O "dia 1 de 67" já
                      está no topo do card; repetir aquele contador aqui faria
                      a barra parecer o andamento do protocolo. */}
                  {mostrado.total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 9 }}>
                      <div style={{ flex: 1, height: 5, borderRadius: 999, background: 'var(--paper-3)', overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.min(100, Math.round((mostrado.paginaAtual / mostrado.total) * 100))}%`,
                          height: '100%', borderRadius: 999, background: 'var(--ember)',
                        }} />
                      </div>
                      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--ink-3)', flexShrink: 0 }}>
                        {Math.min(100, Math.round((mostrado.paginaAtual / mostrado.total) * 100))}%
                      </div>
                    </div>
                  )}

                  <button onClick={onAbrirLivro} style={{
                    width: '100%', marginTop: 14, padding: '14px 12px', borderRadius: 14, border: 'none',
                    background: 'var(--ember)', color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer',
                    boxShadow: '0 12px 26px -14px var(--ember)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                    <Icon name="book" size={16} color="#fff" />
                    {/* "Abrir na página 12" prometia um leitor dentro do app. Quem
                        lê num livro de papel toca aqui e não acontece o que o
                        botão disse. A instrução exata continua na tela seguinte,
                        que é onde ela é executável. */}
                    {/* UM NOME SÓ PARA A AÇÃO (02/10/2026): "Começar o livro", "Ler
                        agora" e "Registrar leitura" eram a mesma coisa em três
                        lugares, e só o último não soa a abrir um livro no app. */}
                    Registrar leitura
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Terminou o livro e não há próximo na fila. Antes disso o programa
              seguia oferecendo "Ler agora" num livro acabado, e registrar a
              leitura respondia erro — para sempre. Terminar um livro é um bom
              momento; ele merece uma pergunta, não um beco. */}
          {precisaEscolher && (
            <div style={{
              marginTop: 16, padding: '18px 18px 16px', borderRadius: 20,
              background: 'var(--paper-2)', border: '1px solid var(--ember)',
            }}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--ember)' }}>
                LIVRO TERMINADO
              </div>
              <div style={{ fontFamily: serif, fontSize: 22, lineHeight: 1.2, color: 'var(--ink)', margin: '8px 0 6px' }}>
                Qual é o próximo?
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-3)', margin: '0 0 14px' }}>
                Sua sequência não depende de terminar livro nenhum — ela depende de
                você abrir algum hoje.
              </p>
              <button onClick={onTrocarLivro} style={{
                width: '100%', padding: '14px 18px', borderRadius: 999, border: 'none',
                background: 'var(--ember)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>Escolher o próximo livro</button>
            </div>
          )}

          {/* Invariante 3 — daqui pra frente não existe mais nenhuma escolha.
              Sem livro na mão não há bloco de capa, e o CTA volta a ocupar a
              largura toda: é a única coisa na tela. */}
          {!precisaEscolher && !mostrado && <button onClick={onAbrirLivro} style={{
            width: '100%', marginTop: 18, padding: '18px 20px', borderRadius: 999, border: 'none',
            background: 'var(--ember)', color: '#fff', fontSize: 16.5, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 14px 32px -14px var(--ember)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
          }}>
            <Icon name="book" size={18} color="#fff" />
            {/* Sem bloco de capa é porque não há livro nenhum — nem do programa,
                nem na estante. "Começar o livro" perguntaria qual. */}
            Escolher um livro
          </button>}
          {!precisaEscolher && (
            <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 10 }}>
              Uma página vence o dia.
            </div>
          )}

          {/* A permissão de abandonar, oferecida ATIVAMENTE por volta da página
              50. A taxa de conclusão é função do livro, não do leitor — insistir
              num livro errado é o jeito mais comum de perder o hábito inteiro. */}
          {ofereceTroca && onTrocarLivro && (
            <button onClick={onTrocarLivro} style={{
              width: '100%', marginTop: 14, padding: '12px 14px', borderRadius: 14,
              background: 'transparent', border: '1px dashed var(--line-strong)',
              color: 'var(--ink-3)', fontSize: 12.5, lineHeight: 1.5, cursor: 'pointer', textAlign: 'left',
            }}>
              Este livro está te prendendo? Se não estiver, <span style={{ color: 'var(--ember)' }}>trocar
              agora é curadoria</span> — e não quebra nada da sua sequência.
            </button>
          )}
        </>)}

        {/* ── Marcas e semana ─────────────────────────────────────────── */}
        {(seq.diasLidos > 0 || venceu) && (
          <div style={{
            marginTop: 20, paddingTop: 18,
            borderTop: `1px solid ${escuro ? 'rgba(245,241,234,0.12)' : 'var(--line)'}`,
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            <Marcas seq={seq} escuro={escuro} />
            <Semana dias={diasComCheckin} hoje={hoje} escuro={escuro} />
          </div>
        )}

        {/* Quando a proteção entrou sozinha, a pessoa precisa SABER — senão ela
            acha que se safou por sorte, e da próxima vez conta com a sorte. */}
        {protegeuOntem && (
          <div style={{
            marginTop: 14, padding: '12px 14px', borderRadius: 14,
            background: escuro ? 'rgba(245,241,234,0.07)' : 'var(--paper)',
            border: `1px solid ${escuro ? 'rgba(245,241,234,0.10)' : 'var(--line)'}`,
            display: 'flex', gap: 9, alignItems: 'flex-start',
          }}>
            <Icon name="lock" size={14} color="var(--ember)" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 13, lineHeight: 1.5, color: escuro ? 'rgba(245,241,234,0.7)' : 'var(--ink-2)' }}>
              Ontem passou em branco e eu cobri com um dos seus dias protegidos.
              Sua sequência continua de pé.
            </div>
          </div>
        )}

        {/* A nota do estágio. No estágio 4 ela é essencial: a curva está
            achatando e a pessoa vai sentir que "não está evoluindo mais". Dizer
            isso antes previne que a assíntota seja lida como fracasso — e avisar
            que os lembretes vão diminuir impede que a redução pareça abandono. */}
        {!venceu && !semPrograma && notaDoEstagio && (
          <div style={{
            marginTop: 16, paddingTop: 14,
            borderTop: `1px solid ${escuro ? 'rgba(245,241,234,0.10)' : 'var(--line)'}`,
            fontSize: 12.5, lineHeight: 1.55, color: 'var(--ink-3)',
          }}>
            {notaDoEstagio}
          </div>
        )}

        {/* O perdão fica visível ANTES de ser preciso — sem ritual de resgate. */}
        {!venceu && !protegeuOntem && estado.protecoes && estado.protecoes.restantes > 0 && seq.atual >= 2 && (
          <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <Icon name="lock" size={13} color="var(--ink-4)" />
            {estado.protecoes.restantes === 1
              ? 'Você tem 1 dia protegido guardado este mês.'
              : `Você tem ${estado.protecoes.restantes} dias protegidos guardados este mês.`}
          </div>
        )}
      </div>

      <OverlayStoryModal
        aberto={overlayAberto}
        onFechar={() => setOverlayAberto(false)}
        dados={{
          tipo: 'protocolo',
          // `dia` é nulo antes do programa começar; o adesivo trata isso como
          // dia 1, que é a leitura correta de quem acabou de entrar.
          dia: dia || 1,
          marco: diaMarco,
          estagio: formado ? 'A Identidade' : estagio.nome,
          chama: seq.atual,
          diasLidos: seq.diasLidos,
          formado,
          titulo: mostrado?.titulo || null,
          autor: mostrado?.autor || null,
          capaUrl: mostrado?.capaUrl || null,
        }}
      />
    </div>
  );
};

export default HeroProtocolo;
